// iter360f — Copy Site Inventory Dialog
//
// Allows an admin to copy every assigned product from the current site
// to one or more destination sites and/or groups. Designed to be drop-in
// usable from anywhere we want to expose a "Copy products to..." action;
// today it is mounted on SiteInventoryTab.jsx.
//
// Flow:
//   1. User clicks the button → dialog opens, fetches sites + groups.
//   2. User searches/filters and toggles destinations (sites or groups).
//   3. On Continue: dry-run `/beverage-inventory/preview-copy`.
//      - If no destination has any existing assignments → run the copy
//        directly in 'additive' mode and show a success toast.
//      - If any destination already has products → show a second dialog
//        asking whether to (a) add only new products (default, recommended)
//        or (b) replace all existing assignments with the source.
//   4. On confirm → POST /beverage-inventory/copy-to with the chosen
//      mode; show a summary toast and call `onSuccess(summary)`.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { Search, Loader2, AlertTriangle, Building2, Users } from 'lucide-react';
import api from '../services/api';
import { getApiError } from '../lib/utils';

export default function CopyInventoryDialog({ open, onOpenChange, sourceSiteId, sourceSiteName, onSuccess }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedSiteIds, setSelectedSiteIds] = useState(new Set());
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
  const [busy, setBusy] = useState(false);
  // Confirmation-dialog state for the "destination has existing" branch.
  const [preview, setPreview] = useState(null);

  // Fetch sites + groups when the dialog opens.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get('/sites').then(r => r.data).catch(() => []),
      api.get('/clients').then(r => r.data).catch(() => []),
    ]).then(([siteList, clientList]) => {
      setSites(Array.isArray(siteList) ? siteList : (siteList?.items || []));
      // Filter clients down to the ones that have at least one site
      // attached — those are the "groups" worth offering as targets.
      const allClients = Array.isArray(clientList) ? clientList : (clientList?.items || []);
      const eligible = allClients.filter(c => (c.is_group || c.kind === 'group' || c.parent_id == null));
      setGroups(eligible);
    }).finally(() => setLoading(false));
  }, [open]);

  const filteredSites = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sites
      .filter(s => s.id !== sourceSiteId)
      .filter(s => !q || (s.name || '').toLowerCase().includes(q) || (s.client_name || '').toLowerCase().includes(q))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [sites, search, sourceSiteId]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups
      .filter(g => !q || (g.name || '').toLowerCase().includes(q))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [groups, search]);

  const toggleSite = (id) => {
    setSelectedSiteIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleGroup = (id) => {
    setSelectedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const targetSummary = `${selectedSiteIds.size} site${selectedSiteIds.size === 1 ? '' : 's'} + ${selectedGroupIds.size} group${selectedGroupIds.size === 1 ? '' : 's'}`;
  const canContinue = !busy && (selectedSiteIds.size > 0 || selectedGroupIds.size > 0);

  const reset = () => {
    setSearch('');
    setSelectedSiteIds(new Set());
    setSelectedGroupIds(new Set());
    setPreview(null);
  };

  const handleContinue = async () => {
    if (!canContinue) return;
    setBusy(true);
    try {
      const payload = {
        target_site_ids: Array.from(selectedSiteIds),
        target_group_ids: Array.from(selectedGroupIds),
        mode: 'additive',
      };
      const prev = await api.post(`/sites/${sourceSiteId}/beverage-inventory/preview-copy`, payload).then(r => r.data);
      if (!prev.targets?.length) {
        toast({ title: 'Nothing to copy', description: 'No valid destination sites resolved.', variant: 'destructive' });
        return;
      }
      if (prev.any_has_existing) {
        setPreview(prev);
        return;
      }
      // Clean path: no existing products at any destination → run additive copy directly.
      await runCopy('additive');
    } catch (err) {
      toast({ title: 'Preview failed', description: getApiError(err, 'Error'), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const runCopy = async (mode) => {
    setBusy(true);
    try {
      const payload = {
        target_site_ids: Array.from(selectedSiteIds),
        target_group_ids: Array.from(selectedGroupIds),
        mode,
      };
      const summary = await api.post(`/sites/${sourceSiteId}/beverage-inventory/copy-to`, payload).then(r => r.data);
      const verb = mode === 'replace' ? 'replaced' : 'added';
      toast({
        title: 'Copy complete',
        description: `${summary.total_added} product(s) ${verb} across ${summary.targets.length} site(s).${summary.total_skipped ? ` ${summary.total_skipped} already present (skipped).` : ''}`,
      });
      onSuccess?.(summary);
      onOpenChange(false);
      reset();
    } catch (err) {
      toast({ title: 'Copy failed', description: getApiError(err, 'Error'), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent className="max-w-2xl" data-testid="copy-inventory-dialog">
        <DialogHeader>
          <DialogTitle>Copy assigned products from {sourceSiteName || 'this site'}</DialogTitle>
          <DialogDescription>
            Pick one or more destination sites and/or groups. Copying to a
            group assigns the source's products to every site in that
            group.
          </DialogDescription>
        </DialogHeader>

        {/* Picker view */}
        {!preview && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                className="pl-8 h-9 text-sm"
                placeholder="Search sites or groups by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="copy-inventory-search"
              />
            </div>

            {loading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Loading destinations...
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[420px] overflow-hidden">
                <div className="border rounded-lg overflow-hidden flex flex-col">
                  <div className="px-3 py-2 bg-muted/40 text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
                    <Users className="h-3.5 w-3.5" /> Groups
                  </div>
                  <div className="flex-1 overflow-y-auto divide-y" data-testid="copy-inventory-group-list">
                    {filteredGroups.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-3">No groups match this search.</p>
                    ) : filteredGroups.map(g => (
                      <label key={g.id} className="flex items-center gap-2 px-3 py-2 hover:bg-muted/30 cursor-pointer">
                        <Checkbox
                          checked={selectedGroupIds.has(g.id)}
                          onCheckedChange={() => toggleGroup(g.id)}
                          data-testid={`copy-inventory-group-${g.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{g.name}</div>
                          {g.site_count != null && (
                            <div className="text-[10px] text-muted-foreground">{g.site_count} site(s)</div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden flex flex-col">
                  <div className="px-3 py-2 bg-muted/40 text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
                    <Building2 className="h-3.5 w-3.5" /> Individual sites
                  </div>
                  <div className="flex-1 overflow-y-auto divide-y" data-testid="copy-inventory-site-list">
                    {filteredSites.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-3">No sites match this search.</p>
                    ) : filteredSites.map(s => (
                      <label key={s.id} className="flex items-center gap-2 px-3 py-2 hover:bg-muted/30 cursor-pointer">
                        <Checkbox
                          checked={selectedSiteIds.has(s.id)}
                          onCheckedChange={() => toggleSite(s.id)}
                          data-testid={`copy-inventory-site-${s.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{s.name}</div>
                          {s.client_name && (
                            <div className="text-[10px] text-muted-foreground truncate">{s.client_name}</div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t">
              <div className="text-xs text-muted-foreground">
                Selected: <span className="font-medium" data-testid="copy-inventory-target-summary">{targetSummary}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={busy}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleContinue} disabled={!canContinue} data-testid="copy-inventory-continue-btn">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                  Continue
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation view — only when at least one destination already has products */}
        {preview && (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-900">Some destinations already have assigned products.</p>
                  <p className="text-amber-800 mt-1">
                    Choose how to merge with the source ({preview.source_count} product
                    {preview.source_count === 1 ? '' : 's'}):
                  </p>
                </div>
              </div>
            </div>

            <div className="max-h-[240px] overflow-y-auto border rounded-lg divide-y" data-testid="copy-inventory-preview-list">
              {preview.targets.map(t => (
                <div key={t.site_id} className="px-3 py-2 flex items-center justify-between text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{t.site_name}</div>
                    {t.group_id && (
                      <Badge variant="secondary" className="text-[10px] mt-1">From group</Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-3 ml-3">
                    <span data-testid={`copy-inventory-preview-existing-${t.site_id}`}>
                      {t.existing_count} existing
                    </span>
                    <span className="text-emerald-700 font-medium" data-testid={`copy-inventory-preview-new-${t.site_id}`}>
                      +{t.new_count} new
                    </span>
                    {t.overlap_count > 0 && (
                      <span className="text-amber-700">{t.overlap_count} overlap</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-lg border bg-muted/30 p-3 text-xs space-y-2">
              <div className="font-semibold uppercase tracking-wider text-[10px]">Merge mode</div>
              <p>
                <span className="font-medium">Add only new</span> — keep every product already
                assigned at the destination and only insert products that aren't there yet (default).
              </p>
              <p>
                <span className="font-medium">Replace all</span> — wipe the destination's inventory
                and replace it with the source's exact list. Existing assignments at the destination
                are lost. Use this only if you intend to mirror the source verbatim.
              </p>
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <Button variant="ghost" size="sm" onClick={() => setPreview(null)} disabled={busy}>
                Back
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => runCopy('replace')}
                  disabled={busy}
                  data-testid="copy-inventory-replace-btn"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                  Replace all
                </Button>
                <Button
                  size="sm"
                  onClick={() => runCopy('additive')}
                  disabled={busy}
                  data-testid="copy-inventory-additive-btn"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
                  Add only new
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter />
      </DialogContent>
    </Dialog>
  );
}
